import { api } from '@/lib/api';
import { PaginatedResponse } from '@/types';

// ── Types ────────────────────────────────────────────────────────────────────

export interface InventoryStats {
  totalItems: number;
  activeItems: number;
  lowStockItems: number;
  outOfStockItems: number;
  totalStockValue: number;
  pendingDOs: number;
  activeDOs: number;
}

export interface LowStockItem {
  id: string;
  code: string;
  name: string;
  category?: string;
  stock: number;
  minStock: number;
  uom: string;
  shortage: number;
}

export interface StockTransaction {
  id: string;
  itemCode: string;
  itemName: string;
  type: string;
  source: string;
  qty: number;
  stockBefore: number;
  stockAfter: number;
  refNo?: string;
  notes?: string;
  createdByName: string;
  createdAt: string;
}

export interface DeliveryOrderListItem {
  id: string;
  no: string;
  salesOrderNo?: string;
  customerName?: string;
  deliveryDate: string;
  deliveryAddress?: string;
  status: string;
  itemCount: number;
  createdByName: string;
  createdAt: string;
}

export interface DeliveryOrderItem {
  id: string;
  itemMasterId: string;
  itemName: string;
  sku?: string;
  qty: number;
  uom: string;
  qtyOrdered: number;
  qtyPreviouslyOut: number;
  currentStock: number;
  notes?: string;
}

export interface DeliveryOrderDetail extends DeliveryOrderListItem {
  salesOrderId?: string;
  customerId?: string;
  recipientName?: string;
  notes?: string;
  items: DeliveryOrderItem[];
}

export interface CreateDOItemRequest {
  itemMasterId: string;
  qty: number;
  uom: string;
  notes?: string;
  sortOrder?: number;
}

export interface CreateDORequest {
  salesOrderId: string;
  customerId?: string;
  deliveryDate: string;
  deliveryAddress?: string;
  recipientName?: string;
  notes?: string;
  items: CreateDOItemRequest[];
}

export interface ShippableSoItem {
  itemMasterId: string;
  itemName: string;
  sku?: string;
  uom: string;
  soQty: number;
  alreadyShipped: number;
  remainingQty: number;
  stockAvailable: number;
}

/** Baris SO yang belum ada Item Master eksplisit (SKU tidak match apa pun) — tidak lagi
 * ditebak dari nama, harus di-link manual lewat linkSoItemToItemMaster(). */
export interface UnmatchedSoItem {
  soItemId: string;
  description: string;
  sku?: string;
  qty: number;
  uom: string;
}

export interface ShippableItemsResult {
  matched: ShippableSoItem[];
  unmatched: UnmatchedSoItem[];
}

export interface RecordStockInRequest {
  itemMasterId: string;
  qty: number;
  refNo?: string;
  refId?: string;
  notes?: string;
  source?: string;
}

// ── Functions ────────────────────────────────────────────────────────────────

export async function getInventoryStats(): Promise<InventoryStats> {
  const res = await api.get<InventoryStats>('/inventory/stats');
  return res.data!;
}

export async function getLowStockItems(): Promise<LowStockItem[]> {
  const res = await api.get<LowStockItem[]>('/inventory/low-stock');
  return res.data ?? [];
}

export async function getStockHistory(params?: {
  itemMasterId?: string;
  page?: number;
  perPage?: number;
  type?: string;
}): Promise<PaginatedResponse<StockTransaction>> {
  return api.getList<StockTransaction>('/inventory/stock-history', params as Record<string, string | number | undefined>);
}

export async function recordStockIn(data: RecordStockInRequest): Promise<StockTransaction> {
  const res = await api.post<StockTransaction>('/inventory/stock-in', data);
  return res.data!;
}

export async function getDeliveryOrders(params?: {
  page?: number;
  perPage?: number;
  search?: string;
  status?: string;
}): Promise<PaginatedResponse<DeliveryOrderListItem>> {
  return api.getList<DeliveryOrderListItem>('/inventory/delivery-orders', params as Record<string, string | number | undefined>);
}

export async function getDeliveryOrderDetail(id: string): Promise<DeliveryOrderDetail> {
  const res = await api.get<DeliveryOrderDetail>(`/inventory/delivery-orders/${id}`);
  return res.data!;
}

export async function createDeliveryOrder(data: CreateDORequest): Promise<DeliveryOrderDetail> {
  const res = await api.post<DeliveryOrderDetail>('/inventory/delivery-orders', data);
  return res.data!;
}

export async function confirmDeliveryOrder(id: string): Promise<DeliveryOrderDetail> {
  const res = await api.post<DeliveryOrderDetail>(`/inventory/delivery-orders/${id}/confirm`, {});
  return res.data!;
}

export async function markDODelivered(id: string): Promise<DeliveryOrderDetail> {
  const res = await api.post<DeliveryOrderDetail>(`/inventory/delivery-orders/${id}/delivered`, {});
  return res.data!;
}

export async function deleteDeliveryOrder(id: string): Promise<void> {
  await api.delete(`/inventory/delivery-orders/${id}`);
}

export async function getShippableItemsForSO(soId: string): Promise<ShippableItemsResult> {
  const res = await api.get<ShippableItemsResult>(`/inventory/delivery-orders/shippable-items/${soId}`);
  return res.data ?? { matched: [], unmatched: [] };
}

export async function linkSoItemToItemMaster(soItemId: string, itemMasterId: string): Promise<void> {
  await api.post(`/inventory/delivery-orders/so-items/${soItemId}/link-item-master`, { itemMasterId });
}

export async function createDOFromSO(soId: string): Promise<DeliveryOrderDetail> {
  const res = await api.post<DeliveryOrderDetail>(
    `/inventory/delivery-orders/from-so/${soId}`,
    {}
  );
  return res.data!;
}
